// app/frontend/application/controllers/page_view_controller.js
//
// Fires a page-view event ping (beacon-style POST) after the user has
// demonstrated basic interaction (click / scroll / keydown / touchstart).
// Most bots skip JS execution entirely; those that run JS are filtered
// server-side in TrackingController (bot UA check, rate-limiting).
//
// Security notes:
//  - Uses standard Rails CSRF protection – reads the token from
//    <meta name="csrf-token"> inserted by csrf_meta_tags in the layout.
//  - keepalive: true ensures the request completes even when the user
//    navigates away immediately after interacting.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    type:  String,
    id:    String,
    debug: Boolean
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    if (!this.csrfToken || !this.typeValue) {
      this._log("missing csrf token or page type; tracking disabled")
      return
    }

    this.sent       = false
    this.interacted = false

    this._onInteraction = this._onInteraction.bind(this)
    this._onPageHide    = this._onPageHide.bind(this)

    window.addEventListener("click",      this._onInteraction, { passive: true })
    window.addEventListener("scroll",     this._onInteraction, { passive: true })
    window.addEventListener("keydown",    this._onInteraction, { passive: true })
    window.addEventListener("touchstart", this._onInteraction, { passive: true })
    window.addEventListener("pagehide",   this._onPageHide)

    this._log("armed", { type: this.typeValue, id: this.idValue })
  }

  disconnect() {
    window.removeEventListener("click",      this._onInteraction)
    window.removeEventListener("scroll",     this._onInteraction)
    window.removeEventListener("keydown",    this._onInteraction)
    window.removeEventListener("touchstart", this._onInteraction)
    window.removeEventListener("pagehide",   this._onPageHide)
  }

  _onInteraction() {
    this.interacted = true
    this._log("interaction detected")
    this._send("interaction")
  }

  _onPageHide() {
    this._log("pagehide")
    this._send("pagehide")
  }

  _send(reason) {
    if (this.sent || !this.interacted) return
    if (!this.csrfToken || !this.typeValue) return

    this.sent = true
    this.disconnect()
    this._log("sending", { reason, type: this.typeValue, id: this.idValue })

    fetch("/track/pageview", {
      method:    "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      body: JSON.stringify({ page_type: this.typeValue, page_id: this.idValue })
    }).catch((error) => {
      this.sent = false
      this._log("send failed", error)
    })
  }

  _log(message, payload = null) {
    if (!this.debugValue) return
    if (payload) {
      console.debug("[page-view]", message, payload)
    } else {
      console.debug("[page-view]", message)
    }
  }
}
